import { motion } from 'motion/react';
import { ArrowDown, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { AppDrawer } from '@/components/AppDrawer';
import { Avatar, Spinner } from '@/components/kit';
import { api } from '@/lib/api';
import { choreName, m } from '@/lib/i18n';
import { useAction } from '@/lib/queries';
import type { AppState } from '@/lib/types';

type Trade = AppState['trades'][number];
type Member = AppState['members'][number];
type ChoreRef = { name: string | null; key: string | null };

function ChoreLine({ label, chore }: { label: string; chore: ChoreRef }) {
  return (
    <div className="bg-muted/60 grid gap-0.5 rounded-2xl px-4 py-3">
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</span>
      <span className="font-semibold">{choreName(chore)}</span>
    </div>
  );
}

/** "You're swapping… for…" (Noom), before asking a housemate. */
export function TradeDrawer({ state, with: other, onClose }: { state: AppState; with: Member | null; onClose: () => void }) {
  const mine = state.dailies.find((d) => d.status === 'pending')?.chore;
  const ask = useAction((id: number) => api('POST', '/trades', { toMemberId: id }), {
    onSuccess: () => {
      toast.success(m.trade_sent({ name: other!.name }));
      onClose();
    },
  });
  return (
    <AppDrawer
      open={!!other}
      onOpenChange={(o) => !o && !ask.isPending && onClose()}
      title={other ? m.trade_title({ name: other.name }) : ''}
      footer={
        <>
          <Button size="lg" onClick={() => ask.mutate(other!.id)} disabled={ask.isPending}>
            {ask.isPending ? <Spinner /> : <ArrowLeftRight />}
            {other && m.trade_ask({ name: other.name })}
          </Button>
          <Button size="lg" variant="ghost" onClick={onClose} disabled={ask.isPending}>
            {m.never_mind()}
          </Button>
        </>
      }
    >
      {other && mine && other.todayChore && (
        <div className="grid justify-items-stretch gap-2">
          <ChoreLine label={m.trade_you_give()} chore={mine} />
          <ArrowDown className="text-muted-foreground size-5 justify-self-center" />
          <ChoreLine label={m.trade_you_get()} chore={other.todayChore} />
        </div>
      )}
    </AppDrawer>
  );
}

/** Open trades on Today: answer one that came in, or wait for / cancel your own (Remote HR's request card). */
export function TradeCards({ trades }: { trades: Trade[] }) {
  const answer = useAction(({ id, accept }: { id: number; accept: boolean }) => api('POST', `/trades/${id}/answer`, { accept }));
  const cancel = useAction((id: number) => api('DELETE', `/trades/${id}`));
  if (!trades.length) return null;
  return (
    <div className="grid gap-3">
      {trades.map((t) => (
        <motion.section key={t.id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-card grid gap-3 rounded-3xl border p-4">
          <div className="flex items-center gap-3">
            <Avatar name={(t.incoming ? t.from : t.to).name} color={(t.incoming ? t.from : t.to).avatar} photo={(t.incoming ? t.from : t.to).photo} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{t.incoming ? m.trade_incoming({ name: t.from.name }) : m.trade_waiting({ name: t.to.name })}</div>
              <div className="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
                {/* Incoming: you'd give your chore (their "get") and receive theirs (their "give"). */}
                <span>{choreName(t.incoming ? t.get : t.give)}</span>
                <ArrowLeftRight className="size-3.5" />
                <span>{choreName(t.incoming ? t.give : t.get)}</span>
              </div>
            </div>
          </div>
          {t.incoming ? (
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => answer.mutate({ id: t.id, accept: false })} disabled={answer.isPending}>
                {answer.isPending && answer.variables?.accept === false && <Spinner />}
                {m.trade_decline()}
              </Button>
              <Button
                onClick={() => answer.mutate({ id: t.id, accept: true }, { onSuccess: () => toast.success(m.trade_done()) })}
                disabled={answer.isPending}
              >
                {answer.isPending && answer.variables?.accept && <Spinner />}
                {m.trade_accept()}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" className="text-muted-foreground" onClick={() => cancel.mutate(t.id)} disabled={cancel.isPending}>
              {cancel.isPending && <Spinner />}
              {m.cancel()}
            </Button>
          )}
        </motion.section>
      ))}
    </div>
  );
}
